import { makeAutoObservable } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { CellRecipeAttributes } from "@/gql/graphql";

import { Cell } from "./Cell";
import { PlannedMealItem } from "./PlannedMealItem";

/**
 * A named group of foods in a cell. Its members live in the cell as regular
 * PlannedMealItems tagged with this group's uuid, so tweaking an ingredient's
 * quantity/unit adjusts the group's macros in this cell only. A group is
 * either linked to a library Recipe (added from "My Recipes" or promoted via
 * "Save to My Recipes") or a purely local label the coach typed over foods.
 */
export class CellRecipe {
  uuid: string;
  /** Denormalized name; survives the source recipe being renamed/deleted. */
  name: string;
  /** Id of the source library Recipe, if any. */
  recipeId?: string;
  /** Local UI state: recipes render collapsed by default. */
  collapsed = true;

  private cell: Cell;

  get items(): PlannedMealItem[] {
    return this.cell.plannedMealItems.filter(
      (item) => item.cellRecipeUuid === this.uuid
    );
  }

  get totalEnergyKcal(): number {
    return this.items.reduce((total, item) => total + item.energyKcal, 0);
  }

  get totalCarbohydrateGrams(): number {
    return this.items.reduce(
      (total, item) => total + item.carbohydrateGrams,
      0
    );
  }

  get totalProteinGrams(): number {
    return this.items.reduce((total, item) => total + item.proteinGrams, 0);
  }

  get totalFatGrams(): number {
    return this.items.reduce((total, item) => total + item.fatGrams, 0);
  }

  constructor(
    cell: Cell,
    name: string,
    recipeId: string | undefined,
    uuid = uuidv4()
  ) {
    makeAutoObservable(this);
    this.cell = cell;
    this.name = name;
    this.recipeId = recipeId;
    this.uuid = uuid;
  }

  /** Whether this group is backed by a recipe in the coach's library. */
  get isLinked(): boolean {
    return !!this.recipeId;
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  setName(name: string): void {
    const trimmed = name.trim();
    if (trimmed) this.name = trimmed;
  }

  /** Link this group to the library recipe it was saved as. */
  linkRecipe(recipeId: string, name: string): void {
    this.recipeId = recipeId;
    this.name = name;
  }

  remove(): void {
    this.cell.removeCellRecipe(this);
  }

  toAttributes(): CellRecipeAttributes {
    return {
      uuid: this.uuid,
      day: this.cell.column.day,
      name: this.name,
      recipeId: this.recipeId,
    };
  }
}
